// src/features/invoice/Invoice.js
import React, { useMemo, useState, useEffect } from "react";
import "./Invoice.css";
import { GrAddCircle } from "react-icons/gr";
import { BsSliders } from "react-icons/bs";
import AddInvoiceModal from "./AddInvoiceModal";
import InvoiceDetail from "./InvoiceDetail";
import SubVendorDetail from "./SubVendorDetail";
import config from "../../config";
import { FiSearch } from "react-icons/fi";
import Header from "../../components/common/Header";


// Client invoice rows will be fetched from API
const sampleClientRows = [];

// Sub vendor rows will be fetched from API
// const sampleSubVendorRows = [];

const Invoice = ({ setActivePage }) => {
  const [activeTab, setActiveTab] = useState("client"); // client | subVendor
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedSubVendorId, setSelectedSubVendorId] = useState('');
  const [clientList, setClientList] = useState([]);
  const [subVendorList, setSubVendorList] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 6;
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [clientRows, setClientRows] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [size, setSize] = useState(6);
  const [userDetails, setUserDetails] = useState({ name: "", role: "", orgId: "", token: "" });
  const [subVendorRows, setSubVendorRows] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);


  useEffect(() => {
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const orgId = localStorage.getItem("orgId") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "ADMIN";
    const token = localStorage.getItem("Authorization") || "";
    setUserDetails({ name: `${firstName} ${lastName}`.trim(), role, orgId, token });
  }, []);

  // Fetch client or sub vendor invoices from API when tab is active
  useEffect(() => {
    if (activeTab === "client") {
      if (selectedClientId === "") {
        setClientRows([]);
        return;
      }

      fetch(`${config.API_BASE_URL}${config.CLIENT_INVOICE_LIST_ENDPOINT}?orgKey=${userDetails.orgId}&clientKey=${selectedClientId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Client invoice data", data);
          // Map API response to table format
          const invoices = Array.isArray(data.body) ? data.body : [];
          const mappedInvoices = invoices.map((inv, idx) => ({
            id: inv.id || idx,
            clientName: inv.clientName,
            clientKey: inv.clientKey,
            orgKey: userDetails.orgId,
            invoiceDateRaw: inv.invoiceDate,
            invoiceDate: inv.invoiceDate
              ? new Date(inv.invoiceDate).toLocaleDateString("en-GB")
              : "-",
            settlementMonth: inv.payrollMonth,
            amount: inv.invoiceAmount,
            invoiceKey: inv.invoiceKey,
            workSite: inv.workSite || "N/A",
            status: inv.status || "Pending",
          }));
          setClientRows(mappedInvoices);
        })
        .catch((err) => {
          console.error("Error fetching client invoices:", err);
          setClientRows([]);
        });
    } else if (activeTab === "subVendor") {
      if (selectedSubVendorId === "") {
        setSubVendorRows([]);
        return;
      }
      fetch(`${config.API_BASE_URL}${config.SUBVENDOR_INVOICE_LIST_ENDPOINT}?orgKey=${userDetails.orgId}&subVendorKey=${selectedSubVendorId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Sub vendor invoice data", data);
          // Map API response to table format
          const invoices = Array.isArray(data.body) ? data.body : Array.isArray(data) ? data : [];
          const mappedInvoices = invoices.map((inv, idx) => ({
            id: inv.id || idx,
            vendorName: inv.subVendorName,
            subVendorKey: inv.subVendorKey || inv.subVendorId,
            orgKey: userDetails.orgId,
            invoiceKey: inv.invoiceKey,
            invoiceDateRaw: inv.invoiceDate,
            invoiceDate: inv.invoiceDate
              ? new Date(inv.invoiceDate).toLocaleDateString("en-GB")
              : "-",
            settlementMonth: inv.settlementMonth,   // ✅ FIXED
            amount: inv.invoiceAmount,
            workSite: inv.workSite || "N/A",
            status: inv.paymentStatus || "Pending", // (optional improvement)
          }));

          setSubVendorRows(mappedInvoices);
        })
        .catch((err) => {
          console.error("Error fetching sub vendor invoices:", err);
          setSubVendorRows([]);
        });
    }
  }, [activeTab, selectedClientId, selectedSubVendorId, userDetails, refreshTrigger]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const orgId = userDetails.orgId;
        if (!orgId || !userDetails.token) return;

        const response = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch clients");
        }

        const data = await response.json();

        // API response array-ah irukum nu assume panren
        setClientList(Array.isArray(data.body) ? data.body : []);

      } catch (error) {
        console.error("Client fetch error:", error);
        setClientList([]);
      }
    };

    fetchClients();
  }, [userDetails.orgId, userDetails.token]);

  useEffect(() => {
    const fetchSubVendors = async () => {
      try {
        const orgId = userDetails.orgId;
        if (!orgId || !userDetails.token) return;

        const response = await fetch(
          `${config.API_BASE_URL}${config.SUBVENDOR_LIST_ENDPOINT}?orgKey=${orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch sub-vendors");
        }

        const data = await response.json();

        // API response array-ah irukum nu assume panren
        setSubVendorList(Array.isArray(data.body) ? data.body : []);

      } catch (error) {
        console.error("Sub-vendor fetch error:", error);
        setSubVendorList([]);
      }
    };

    fetchSubVendors();
  }, [userDetails.orgId, userDetails.token]);

  const rows = useMemo(() => {
    const dataset = activeTab === "subVendor" ? subVendorRows : clientRows;
    const nameKey = activeTab === "subVendor" ? "vendorName" : "clientName";

    let data = dataset.filter((r) =>
      (r[nameKey] || "")?.toLowerCase().includes(search.toLowerCase())
    );

    // Payment Status Filter
    if (paymentStatusFilter) {
      data = data.filter((r) =>
        (r.status || "")?.toLowerCase() === paymentStatusFilter.toLowerCase()
      );
    }

    // Sorting
    if (sortBy === "name") {
      data.sort((a, b) =>
        (a[nameKey] || "").localeCompare(b[nameKey] || "")
      );
    }

    if (sortBy === "amountHigh") {
      data.sort((a, b) => (b.amount || 0) - (a.amount || 0));
    }

    if (sortBy === "amountLow") {
      data.sort((a, b) => (a.amount || 0) - (b.amount || 0));
    }

    if (sortBy === "date") {
      data.sort((a, b) =>
        new Date(a.invoiceDate || 0) - new Date(b.invoiceDate || 0)
      );
    }

    return data;
  }, [search, activeTab, clientRows, subVendorRows, sortBy, paymentStatusFilter]);


  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));
  const pageRows = rows.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    // reset to first page when filters change
    setPage(1);
  }, [search, activeTab]);


  const handleBackToList = () => {
    setSelectedInvoice(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleRowClick = (row) => {
    console.log("Row clicked:", row);
    console.log("SubVendorKey inside row:", row.subVendorKey);
    setSelectedInvoice(row);
  };

  // If an invoice is selected, show detail based on tab
  if (selectedInvoice) {
    if (activeTab === "subVendor") {
      return (
        <InvoiceDetail
          invoice={selectedInvoice}
          selectedClientId={selectedSubVendorId}
          isSubVendor={true}
          onBack={handleBackToList}
          userDetails={userDetails}
        />
      );
    }

    return (
      <InvoiceDetail
        invoice={selectedInvoice}
        selectedClientId={selectedClientId}
        isSubVendor={false}
        onBack={handleBackToList}
        userDetails={userDetails}
      />
    );
  }



  // If an invoice is selected, show the detail view
  // if (selectedInvoice) {
  //   if (activeTab === "subVendor") {
  //     return (
  //       <SubVendorDetail
  //         invoice={selectedInvoice}
  //         onBack={handleBackToList}
  //         userDetails={userDetails}
  //       />
  //     );
  //   }
  //   return (
  //     <InvoiceDetail
  //       invoice={selectedInvoice}
  //       isSubVendor={false}
  //       onBack={handleBackToList}
  //       userDetails={userDetails}
  //     />
  //   );
  // }

  return (
    <div className="invoice-container">
      {/* Header card (shared header class styling) */}
      <Header
        title="Invoice"
        subtitle="Invoice Information"
        onProfileClick={() => setActivePage && setActivePage("Profile")}
      />

      {/* Bottom main card */}
      <div className="invoice-card">
        <div className="toolbar">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

            {/* Search input with icon */}
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center"
              }}
            >
              <FiSearch
                style={{
                  position: "absolute",
                  left: "12px",
                  fontSize: "14px",
                  color: "#999"
                }}
              />

              <input
                className="search-box"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  paddingLeft: "35px",
                  width: "180px",
                  height: "15px"
                }}
              />
            </div>

            {/* Client/Sub-Vendor Dropdown based on active tab */}
            {activeTab === "client" ? (
              <select
                className="search-input"
                style={{ width: '180px', height: "38px" }}
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
              >
                <option value="">All Clients</option>
                {clientList.map(client => (
                  <option key={client.clientKey} value={client.clientKey}>
                    {client.name}
                  </option>
                ))}
              </select>
            ) : (
              <select
                className="search-input"
                style={{ width: '180px', height: "36px" }}
                value={selectedSubVendorId}
                onChange={(e) => setSelectedSubVendorId(e.target.value)}
              >
                <option value="">All Sub-Vendors</option>
                {subVendorList.map(vendor => (
                  <option key={vendor.subVendorId} value={vendor.subVendorId}>
                    {vendor.subVendorName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tabs each in its own container */}
          <div className="tabs12">
            <div className="tab-item">
              <button
                className={`tab-btn ${activeTab === "client" ? "active" : ""}`}
                onClick={() => setActiveTab("client")}
              >
                <span>Client</span>
              </button>
            </div>
            <div className="tab-item">
              <button
                className={`tab-btn ${activeTab === "subVendor" ? "active" : ""}`}
                onClick={() => setActiveTab("subVendor")}
              >
                <span>Sub Vendor</span>
              </button>
            </div>
          </div>

          {/* Right controls separated */}
          <div className="right-controls">
            <div className="add-wrapper">
              <button className="add-btn" onClick={() => setShowAddModal(true)}>
                <GrAddCircle /> <span>Add Invoice</span>
              </button>
            </div>
            <div className="filter-wrapper">
              <button className="filter-btn" onClick={() => setShowFilter(true)}>
                <span><BsSliders /> Filter</span>
              </button>

            </div>
          </div>
        </div>
        {showFilter && (
          <div className="filter-overlay" onClick={() => setShowFilter(false)}>
            <div
              className="filter-card"
              onClick={(e) => e.stopPropagation()}
            >
              <h4>Filter</h4>

              <label>Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => {
                  const value = e.target.value;
                  setSortBy("invoiceDate");

                  if (value === "newest") {
                    setSortOrder("DESC");
                  } else if (value === "oldest") {
                    setSortOrder("ASC");
                  }
                }}
              >
                <option value=""></option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>


              <div className="filter-buttons">
                <button
                  className="clear-btn"
                  onClick={() => {
                    setSortBy("");
                    setPaymentStatusFilter("");
                    setShowFilter(false);
                  }}
                >
                  Clear
                </button>

                <button
                  className="apply-btn"
                  onClick={() => {

                    // CLIENT TAB
                    if (activeTab === "client") {
                      fetch(`${config.API_BASE_URL}${config.CLIENT_INVOICE_LIST_ENDPOINT}?orgKey=${userDetails.orgId}&clientKey=${selectedClientId}&page=0&sortOrder=${sortOrder}&sortBy=${sortBy}&size=${size}`, {
                        method: "GET",
                        headers: {
                          Authorization: `Bearer ${userDetails.token}`,
                          "Content-Type": "application/json",
                        },
                      })
                        .then((res) => res.json())
                        .then((data) => {
                          const invoices = Array.isArray(data.body) ? data.body : [];

                          const mappedInvoices = invoices.map((inv, idx) => ({
                            id: inv.id || idx,
                            clientName: inv.clientName,
                            clientKey: inv.clientKey,
                            orgKey: userDetails.orgId,
                            invoiceDate: inv.invoiceDate
                              ? new Date(inv.invoiceDate).toLocaleDateString("en-GB")
                              : "-",
                            settlementMonth: inv.payrollMonth,
                            amount: inv.invoiceAmount,
                            invoiceKey: inv.invoiceKey,
                            workSite: inv.workSite || "N/A",
                            status: inv.status || "Pending",
                          }));

                          setClientRows(mappedInvoices);
                        });
                    }

                    // SUB VENDOR TAB
                    if (activeTab === "subVendor") {
                      fetch(`${config.API_BASE_URL}${config.SUBVENDOR_INVOICE_LIST_ENDPOINT}?orgKey=${userDetails.orgId}&subVendorKey=${selectedSubVendorId}&page=0&sortOrder=${sortOrder}&sortBy=${sortBy}&size=${size}`, {
                        method: "GET",
                        headers: {
                          Authorization: `Bearer ${userDetails.token}`,
                          "Content-Type": "application/json",
                        },
                      })
                        .then((res) => res.json())
                        .then((data) => {
                          const invoices = Array.isArray(data.body) ? data.body : [];

                          const mappedInvoices = invoices.map((inv, idx) => ({
                            id: inv.id || idx,
                            vendorName: inv.subVendorName,
                            subVendorKey: inv.subVendorKey,
                            orgKey: userDetails.orgId,
                            invoiceDate: inv.invoiceDate
                              ? new Date(inv.invoiceDate).toLocaleDateString("en-GB")
                              : "-",
                            settlementMonth: inv.settlementMonth,
                            amount: inv.invoiceAmount,
                            invoiceKey: inv.invoiceKey,
                            workSite: inv.workSite || "N/A",
                            status: inv.paymentStatus || "Pending",
                          }));

                          setSubVendorRows(mappedInvoices);
                        });
                    }

                    setShowFilter(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <table className="invoice-table">
          <thead>
            <tr>
              <th>{activeTab === "subVendor" ? "Sub Vendor Name" : "Client Name"}</th>
              <th>Invoice Date</th>
              <th>Settlement Month</th>
              <th>Invoice Amount</th>
              <th>Invoice Key</th>
              <th>Work Site</th>
              <th>Payment Status</th>
            </tr>
          </thead>
          <tbody>
            {activeTab === "client" && selectedClientId === "" ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#060505ff", fontSize: "11px", fontWeight: "500" }}>
                  Please select a client to view data
                </td>
              </tr>
            ) : activeTab === "subVendor" && selectedSubVendorId === "" ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#070707ff", fontSize: "11px", fontWeight: "500" }}>
                  Please select a sub vendor to view data
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#0a0909ff", fontSize: "11px", fontWeight: "500" }}>
                  No invoices found.
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                <tr key={r.id} onClick={() => handleRowClick(r)} style={{ cursor: "pointer" }}>
                  <td>{activeTab === "subVendor" ? r.vendorName : r.clientName}</td>
                  <td>{r.invoiceDate}</td>
                  <td>{r.settlementMonth}</td>
                  <td>{r.amount}</td>

                  <td>{r.invoiceKey}</td>
                  <td>{r.workSite}</td>
                  <td>
                    <span
                      className={`status-pill ${r.status.toLowerCase().includes("paid") &&
                        !r.status.toLowerCase().includes("un ")
                        ? "paid"
                        : "unpaid"
                        }`}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="table-footer">
          <div className="record-count">
            Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, rows.length)} of {rows.length} entries
          </div>
          <div className="pagination">
            <button
              className="pagination-button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-button ${page === i + 1 ? "active" : ""}`}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
      <AddInvoiceModal
        open={showAddModal}
        isSubVendor={activeTab === "subVendor"}
        onClose={() => setShowAddModal(false)}
        onGenerate={(payload) => {
          // Re-trigger fetch or rely on parent logic
          console.log("Generate invoice payload", payload);
          setRefreshTrigger(prev => prev + 1);
          // Let AddInvoiceModal handle its own close after popup using onClose()
        }}
      />
    </div>
  );
};

export default Invoice;
